import { App, Form } from "antd";
import { FC, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    getApiError,
    getPointInfoCollection,
    postSchedulerCreateRouteByPoints,
} from "tm-shared/api";
import { Point, UserRole } from "tm-shared/types";
import { arrayToRecord } from "tm-shared/utils";

import { ROUTES } from "@/constants";
import { useAsyncAction } from "@/hooks";
import {
    useAppDispatch,
    useAppSelector,
    useProtectedUserRoleSelector,
} from "@/hooks/redux";
import { BlockList } from "@/shared/pageComposition/BlockList";

import { RouteEditorFormValues } from "../components/RouteEditorForm";
import { useScheduleSelection } from "../hooks";
import {
    getRouteEditorCapabilities,
    RouteEditorCapabilities,
} from "../RouteEditor.policy";
import { RouteEditorModalState } from "../RouteEditor.types";
import {
    getRouteEditorCreateBlocks,
    RouteEditorCreateModel,
} from "./RouteEditorCreate.blocks";

interface RouteEditorCreateProps {
    capabilities?: RouteEditorCapabilities;
}

export const RouteEditorCreate: FC<RouteEditorCreateProps> = ({
    capabilities: propCapabilities,
}) => {
    const { notification } = App.useApp();

    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();

    const role = useProtectedUserRoleSelector();
    const capabilities = useMemo(
        () => propCapabilities ?? getRouteEditorCapabilities(role),
        [propCapabilities, role],
    );

    const currentScheduleList = useAppSelector(
        (s) => s.manager.currentScheduleList,
    );
    const [points, setPoints] = useState<Record<string, Point>>({});

    const [modalState, setModalState] = useState<RouteEditorModalState>(null);
    const [schedulesLoading, setSchedulesLoading] = useState(false);

    const [routeEditorForm] = Form.useForm<RouteEditorFormValues>();

    useEffect(() => {
        if (location?.state?.formValues) {
            routeEditorForm.setFieldsValue(location.state.formValues);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.key, routeEditorForm]);

    const pointIds = useMemo(() => {
        if (!currentScheduleList.length) return [];

        return currentScheduleList.map((i) => i.pointId).sort();
    }, [currentScheduleList]);

    const hasMissingPoint = useMemo(() => {
        if (!pointIds.length) return false;

        return pointIds.some((id) => !points[id]);
    }, [pointIds, points]);

    useEffect(() => {
        const load = async () => {
            if (!hasMissingPoint) return;

            try {
                setSchedulesLoading(true);
                const { result: pointsData } = await getPointInfoCollection({
                    ids: pointIds,
                });

                const pointsMap = arrayToRecord(pointsData ?? [], "id");

                setPoints(pointsMap);
            } finally {
                setSchedulesLoading(false);
            }
        };
        load();
    }, [dispatch, pointIds, hasMissingPoint]);

    const selection = useScheduleSelection({ points });

    const [handleSave, saveLoading] = useAsyncAction(async () => {
        const { routeAgent, routeName } = routeEditorForm.getFieldsValue();

        try {
            await postSchedulerCreateRouteByPoints({
                name: routeName,
                agentId: routeAgent,
                schedules: currentScheduleList,
                roleSlug: role === UserRole.Sv ? UserRole.Ta : UserRole.Md,
            });

            navigate(ROUTES.RouteManagement);
        } catch (error) {
            const { displayMessage } = getApiError(error);

            notification.error({
                title: "Не вдалося створити маршрут",
                description: displayMessage,
            });
        }
    });

    const model: RouteEditorCreateModel = useMemo(
        () => ({
            capabilities,
            schedulesLoading,
            saveLoading,
            routeEditorForm,
            points,
            modalState,
            selection,
            handlers: {
                handleSave,
                setModalState,
            },
        }),
        [
            capabilities,
            schedulesLoading,
            saveLoading,
            routeEditorForm,
            points,
            modalState,
            selection,
            handleSave,
        ],
    );

    const blocks = useMemo(() => getRouteEditorCreateBlocks(model), [model]);

    return <BlockList blocks={blocks} model={model} />;
};
